import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import {
  Terminal, Check, X, ArrowRight, Loader2, ShieldAlert, Sparkles, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import InvitePreviewCard, { inviteRoleLabelFor } from "../components/InvitePreviewCard";

/**
 * Public invitation preview page.
 *
 *   URL:   /invite/:eventId?email=<invitee-email>
 *
 * Renders event details, the invited role, and (when the visitor is signed
 * in) Accept / Decline actions. Unauthenticated visitors are prompted to
 * sign in; the return-path is captured via sessionStorage so they land back
 * on this page after auth completes.
 */
export default function InvitePage() {
  const { eventId } = useParams();
  const [params] = useSearchParams();
  const emailParam = params.get("email") || "";
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [action, setAction] = useState("idle"); // idle | accepting | declining

  const load = useCallback(async () => {
    setLoading(true); setNotFound(false);
    try {
      const { data } = await api.get(`/invite/${eventId}`, {
        params: emailParam ? { email: emailParam } : {},
      });
      setPreview(data);
    } catch (e) {
      if (e.response?.status === 404) setNotFound(true);
      else toast.error("Could not load the invitation. Try the link in your email again.");
    } finally {
      setLoading(false);
    }
  }, [eventId, emailParam]);

  useEffect(() => { load(); }, [load]);

  const stashReturnAndSignIn = () => {
    try {
      const target = `/invite/${eventId}${emailParam ? `?email=${encodeURIComponent(emailParam)}` : ""}`;
      sessionStorage.setItem("post_login_return", target);
    } catch (e) { console.debug("post_login_return set failed", e); }
    const inviteQuery = `invite_event=${encodeURIComponent(eventId)}`
      + `&title=${encodeURIComponent(preview?.event?.title || "this event")}`
      + `&role=${encodeURIComponent(inviteRoleLabel)}`;
    navigate(`/?${inviteQuery}`);
  };

  const onAccept = async () => {
    setAction("accepting");
    try {
      await api.post(`/invite/${eventId}/accept`);
      toast.success("Invitation accepted. Welcome to the event.");
      navigate(`/events/${eventId}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not accept the invitation.");
      setAction("idle");
    }
  };

  const onDecline = async () => {
    setAction("declining");
    try {
      await api.post(`/invite/${eventId}/decline`);
      toast("Invitation declined.");
      navigate(user ? "/dashboard" : "/");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not decline the invitation.");
      setAction("idle");
    }
  };

  const inviteRoleLabel = useMemo(() => inviteRoleLabelFor(preview?.member), [preview]);

  if (loading || authLoading) {
    return <FullscreenLoading label="Loading invitation…" />;
  }
  if (notFound) {
    return (
      <FullscreenState
        icon={<ShieldAlert size={32} strokeWidth={1.5} />}
        title="This invitation link isn't valid."
        subtitle="Ask the event planner to resend your invite, or double-check the link from your email."
        cta={<Link to="/"><Button className="rounded-sm">Go to AtelisOS</Button></Link>}
      />
    );
  }

  const ev = preview.event;
  const status = preview.member?.invite_status;
  const alreadyAccepted = status === "accepted";
  const alreadyDeclined = status === "declined";
  const inviteeMissing = !preview.member;

  return (
    <div className="min-h-screen bg-canvas flex items-start justify-center p-4 md:p-10" data-testid="invite-page">
      <div className="w-full max-w-3xl">
        {/* Top strip — brand */}
        <div className="flex items-center justify-between mb-6 font-mono text-[10px] tracking-[0.2em] uppercase text-muted">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-[#0A0A0A] flex items-center justify-center">
              <img src="/atelis-icon-192.png" alt="AtelisOS" className="w-full h-full object-contain" />
            </div>
            <span>AtelisOS · Event collaboration</span>
          </div>
          <span className="hidden sm:inline">INVITE // {eventId?.slice(-8)}</span>
        </div>

        <div className="bevel bg-surface p-6 md:p-10 border border-hair">
          {/* Header */}
          <div className="mb-6">
            <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted mb-3 flex items-center gap-1.5">
              <Sparkles size={12} strokeWidth={2} /> YOU&apos;RE INVITED
            </div>
            <h1 className="font-display text-4xl md:text-5xl font-black tracking-tight text-app leading-[0.98]">
              {ev.title}
            </h1>
            {preview.invited_by?.name ? (
              <p className="text-sm text-muted mt-3 leading-relaxed" data-testid="invite-role-line">
                <span className="text-app">{preview.invited_by.name}</span> invited you to collaborate as{" "}
                <span className="text-app font-medium">{inviteRoleLabel}</span> through AtelisOS.
              </p>
            ) : (
              <p className="text-sm text-muted mt-3 leading-relaxed">
                You&apos;ve been invited to collaborate as{" "}
                <span className="text-app font-medium">{inviteRoleLabel}</span>.
              </p>
            )}
          </div>

          <InvitePreviewCard preview={preview} />

          <div className="mt-8">
          {/* Actions */}
          {alreadyAccepted ? (
            <StatusBanner
              tone="success"
              title="Invitation already accepted."
              subtitle="Head to the event workspace to see your dashboard, tasks, and timeline."
              action={
                <Link to={`/events/${eventId}`}>
                  <Button className="rounded-sm bg-[#0A0A0A] hover:bg-black text-white h-11 px-5" data-testid="invite-open-portal-btn">
                    Open event workspace <ExternalLink size={14} className="ml-2" />
                  </Button>
                </Link>
              }
            />
          ) : alreadyDeclined ? (
            <StatusBanner
              tone="muted"
              title="You previously declined this invitation."
              subtitle="If this was a mistake, ask the planner to resend the invite."
              action={<Link to={user ? "/dashboard" : "/"}><Button variant="outline" className="rounded-sm h-11 px-5">Back to AtelisOS</Button></Link>}
            />
          ) : inviteeMissing && user ? (
            <StatusBanner
              tone="warn"
              title="This invite isn't attached to your account."
              subtitle={`Sign out and sign back in using ${emailParam || "the email address the invite was sent to"} to see it.`}
              action={<Link to="/dashboard"><Button variant="outline" className="rounded-sm h-11 px-5">Go to dashboard</Button></Link>}
            />
          ) : user ? (
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={onAccept}
                disabled={action !== "idle"}
                className="rounded-sm bg-[#0A0A0A] hover:bg-black text-white h-12 px-6 justify-between font-medium tracking-wide flex-1"
                data-testid="invite-accept-btn"
              >
                <span className="flex items-center gap-2">
                  {action === "accepting" ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                  Accept invitation
                </span>
                <ArrowRight size={15} strokeWidth={2} />
              </Button>
              <Button
                onClick={onDecline}
                disabled={action !== "idle"}
                variant="outline"
                className="rounded-sm h-12 px-6 border-hair"
                data-testid="invite-decline-btn"
              >
                {action === "declining" ? <Loader2 size={16} className="animate-spin mr-2" /> : <X size={16} className="mr-2" />}
                Decline
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              <Button
                onClick={stashReturnAndSignIn}
                className="w-full rounded-sm bg-[#0A0A0A] hover:bg-black text-white h-12 justify-between px-4 font-medium tracking-wide"
                data-testid="invite-sign-in-btn"
              >
                <span>Sign in or create a free account to continue</span>
                <ArrowRight size={15} strokeWidth={2} />
              </Button>
              <p className="text-xs text-muted leading-relaxed">
                {emailParam ? (<>Use <span className="text-app font-medium">{emailParam}</span> — the address this invite was sent to.</>) : "Use the email address the invite was sent to."}
                {" "}Signing in creates your AtelisOS account if you don&apos;t already have one.
              </p>
            </div>
          )}
          </div>
        </div>

        <div className="mt-4 text-center font-mono text-[10px] tracking-[0.2em] uppercase text-muted">
          Powered by AtelisOS · Event Operating System
        </div>
      </div>
    </div>
  );
}

// ============ Helpers ============

function StatusBanner({ tone, title, subtitle, action }) {
  const toneClasses = {
    success: "border-emerald-500/40 bg-emerald-50/50 dark:bg-emerald-500/5",
    muted: "border-hair bg-inset",
    warn: "border-amber-500/40 bg-amber-50/50 dark:bg-amber-500/5",
  }[tone] || "border-hair bg-inset";
  return (
    <div className={`border rounded-sm p-4 flex flex-col md:flex-row md:items-center gap-4 justify-between ${toneClasses}`}>
      <div>
        <div className="text-app font-medium">{title}</div>
        {subtitle && <div className="text-sm text-muted mt-1 leading-relaxed">{subtitle}</div>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}

function FullscreenLoading({ label }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-canvas">
      <div className="text-center">
        <Loader2 size={28} className="animate-spin mx-auto text-muted mb-3" />
        <div className="eyebrow text-muted">{label}</div>
      </div>
    </div>
  );
}

function FullscreenState({ icon, title, subtitle, cta }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-canvas p-6">
      <div className="max-w-md text-center">
        <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center border border-hair rounded-full bg-inset text-muted">
          {icon}
        </div>
        <h1 className="font-display text-2xl font-black text-app mb-2">{title}</h1>
        <p className="text-sm text-muted leading-relaxed mb-6">{subtitle}</p>
        {cta}
      </div>
    </div>
  );
}
